/**
 * Client-side validation for AI context groups (mirrors supernova context-group-validator).
 */

export const MIN_GROUP_NAME_LENGTH = 4;
export const MIN_CONTEXT_LENGTH = 4;
export const MAX_GROUP_NAME_LENGTH = 120;
export const MAX_CONTEXT_LENGTH = 10000;

const PROMPT_INJECTION_PATTERNS = [
  /ignore\s+(all\s+)?(previous|prior|above)\s+(instructions?|prompts?|rules?)/i,
  /disregard\s+(all\s+)?(previous|prior|above)/i,
  /forget\s+(everything|all)\s+(you\s+)?(know|learned|were\s+told)/i,
  /\byou\s+are\s+now\b/i,
  /\bact\s+as\s+(a\s+)?(?!an?\s+accountant)/i,
  /\bsystem\s*:\s*/i,
  /\bassistant\s*:\s*/i,
  /<\|im_start\|>/i,
  /<\|im_end\|>/i,
  /\[INST\]/i,
  /\[\/INST\]/i,
  /###\s*instruction/i,
  /\bnew\s+instructions?\s*:/i,
  /\boverride\s+(the\s+)?(system|previous)\b/i,
  /\bjailbreak\b/i,
  /\bdo\s+anything\s+now\b/i,
  /\bDAN\s+mode\b/i,
  /\brole\s*:\s*system\b/i,
];

export const containsPromptInjection = (text) => {
  const normalized = (text || '').toString().trim();
  if (!normalized) return false;
  return PROMPT_INJECTION_PATTERNS.some((pattern) => pattern.test(normalized));
};

/**
 * @param {Object} form
 * @returns {{ valid: boolean, errors: Record<string, string> }}
 */
export const validateContextGroupForm = (form = {}) => {
  const errors = {};
  const groupName = (form.group_name || '').trim();
  const context = (form.context || '').trim();
  const glCodes = Array.isArray(form.gl_codes) ? form.gl_codes : [];
  const networks = Array.isArray(form.networks) ? form.networks : [];

  if (!groupName) {
    errors.group_name = 'Category name is required.';
  } else if (!/[A-Za-z0-9]/.test(groupName)) {
    // Reject values made only of special characters / whitespace (e.g. @#$%^&*)
    errors.group_name = 'Category name must contain at least one letter or number.';
  } else if (groupName.length < MIN_GROUP_NAME_LENGTH) {
    errors.group_name = `Category name must be at least ${MIN_GROUP_NAME_LENGTH} characters.`;
  } else if (groupName.length > MAX_GROUP_NAME_LENGTH) {
    errors.group_name = `Category name must not exceed ${MAX_GROUP_NAME_LENGTH} characters.`;
  } else if (containsPromptInjection(groupName)) {
    errors.group_name = 'Category name contains disallowed instruction-like content.';
  }

  if (glCodes.length === 0) {
    errors.gl_codes = 'Select at least one GL code.';
  } else {
    const bad = glCodes.find((item) => {
      const code = (item?.code || '').toString().trim();
      const name = (item?.name || item?.description || '').toString().trim();
      return !code || !name;
    });
    if (bad) {
      errors.gl_codes = 'Each GL must have a non-empty code and description.';
    }
  }

  if (networks.length === 0) {
    errors.networks = 'Select at least one network.';
  }

  if (!context) {
    errors.context = 'Context is required.';
  } else if (context.length < MIN_CONTEXT_LENGTH) {
    errors.context = `Context must be at least ${MIN_CONTEXT_LENGTH} characters.`;
  } else if (context.length > MAX_CONTEXT_LENGTH) {
    errors.context = `Context must not exceed ${MAX_CONTEXT_LENGTH} characters.`;
  } else if (containsPromptInjection(context)) {
    errors.context = 'Context contains disallowed instruction-like content that could affect AI behavior.';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Normalize picker / Mongo id values to a stable string for Set/Map/includes checks.
 * @param {*} value
 * @returns {string}
 */
export const normalizePickerId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid);
    if (typeof value.toHexString === 'function') return value.toHexString();
    if (typeof value.toString === 'function') {
      const asString = value.toString();
      if (/^[a-f0-9]{24}$/i.test(asString)) return asString;
    }
  }
  return String(value);
};

export const pickerIdsEqual = (a, b) => {
  const left = normalizePickerId(a);
  const right = normalizePickerId(b);
  return Boolean(left) && left === right;
};

export const mergePickerItems = (existing = [], incoming = [], idKey = 'value') => {
  const map = new Map();
  const toKey = (item, incomingItem = false) => {
    const raw = incomingItem
      ? (item[idKey] || item.value || item.gl_id || item.network_id)
      : (item[idKey] || item.gl_id || item.network_id || item.code || item.name);
    return normalizePickerId(raw) || (raw != null && raw !== '' ? String(raw) : '');
  };
  for (const item of existing) {
    const key = toKey(item, false);
    if (key) map.set(key, item);
  }
  for (const item of incoming) {
    const key = toKey(item, true);
    if (!key) continue;
    map.set(key, item);
  }
  return Array.from(map.values());
};

export const formatGlChipLabel = (item = {}) => {
  const code = (
    item.code
    || item.glcode
    || item.gl_code
    || item.glCode
    || ''
  ).toString().trim();
  const name = (
    item.name
    || item.description
    || item.gl_description
    || item.text
    || ''
  ).toString().trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || '';
};

export const formatNetworkChipLabel = (item = {}) => (
  item.name || item.text || item.network || item.code || ''
).toString().trim();

export const formatGroupNameLabel = (item = {}) => (
  item.group_name || item.group_key || ''
).toString().trim();

/** Build sorted { name, value } options for lb-datatablenew column filters. */
export const buildUniqueFilterOptions = (values = []) => {
  const unique = new Set();
  (values || []).forEach((value) => {
    const normalized = (value == null ? '' : String(value)).trim();
    if (normalized) unique.add(normalized);
  });
  return Array.from(unique)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ name: value, value }));
};

export const collectGroupNameFilterOptions = (groups = []) =>
  buildUniqueFilterOptions((groups || []).map((group) => formatGroupNameLabel(group)));

export const collectGlFilterOptions = (groups = []) => {
  const labels = [];
  (groups || []).forEach((group) => {
    (group.gl_codes || []).forEach((gl) => {
      const label = formatGlChipLabel(gl);
      if (label) labels.push(label);
    });
  });
  return buildUniqueFilterOptions(labels);
};

export const collectNetworkFilterOptions = (groups = []) => {
  const labels = [];
  (groups || []).forEach((group) => {
    (group.networks || []).forEach((network) => {
      const label = formatNetworkChipLabel(network);
      if (label) labels.push(label);
    });
  });
  return buildUniqueFilterOptions(labels);
};

/**
 * Client-side filter for context-group rows by column multi-select values.
 * @param {Array} groups
 * @param {{ group_name?: string[], gl_codes?: string[], networks?: string[] }} filters
 */
export const filterContextGroups = (groups = [], filters = {}) => {
  let rows = Array.isArray(groups) ? [...groups] : [];
  const groupNames = filters.group_name || [];
  const glLabels = filters.gl_codes || [];
  const networks = filters.networks || [];

  if (groupNames.length) {
    const selected = new Set(groupNames.map(String));
    rows = rows.filter((row) => selected.has(formatGroupNameLabel(row)));
  }

  if (glLabels.length) {
    const selected = new Set(glLabels.map(String));
    rows = rows.filter((row) =>
      (row.gl_codes || []).some((gl) => selected.has(formatGlChipLabel(gl)))
    );
  }

  if (networks.length) {
    const selected = new Set(networks.map(String));
    rows = rows.filter((row) =>
      (row.networks || []).some((network) => selected.has(formatNetworkChipLabel(network)))
    );
  }

  return rows;
};

export const SUPER_GROUP_OPTIONS = [
  { text: 'PL', value: 'PL' },
  { text: 'BS', value: 'BS' },
  { text: 'STAT', value: 'STAT' },
];

export const reportingTypeFromSuperGroup = (superGroup) => {
  const sg = (superGroup || '').toString().toUpperCase();
  if (sg === 'BS') return 'bs';
  if (sg === 'STAT') return 'stat';
  return 'pl';
};
